<template>
  <div v-if="showComponent">
    <div class="tla-form-group">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="colourKnownLabel"></label>

      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in colourKnownOptions"
          :key="`${item.touchpointValue}-${engineSize}`"
          :collectionName="'basicColourKnown'"
          :inputId="`${item.touchpointValue}-${engineSize}`"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isRadioStyle="true"
          :validationScope="validationScope"
          @basicColourKnown="setBasicColourKnownChoice($event)">
          <div class="generic-touchpoint__text" slot="touchpoint" v-html="item.touchpointName"></div>
        </touchpoint>
      </div>
      <span class="tla-form-group__message " :class="getKnownValidationClass('message')">
        Please select an option.
      </span>
    </div>
    <div class="tla-form-group" v-if="showBasicColours">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="basicColourLabel"></label>

      <div class="tla-form-group__subtext">{{colourRestrictionText}}</div>
      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in basicColourOptions"
          :key="`${item.touchpointValue}-${engineSize}`"
          :collectionName="'basicColours'"
          :inputId="`${item.touchpointValue}-${engineSize}`"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :validationScope="validationScope"
          @basicColours="setBasicColourChoice($event)">
          <div class="detailed-colour__text" slot="touchpoint" v-html="item.touchpointName"></div>
        </touchpoint>
      </div>
      <span class="tla-form-group__message " :class="getBasicValidationClass('message')">
        Please select an option.
      </span>
    </div>

    <button v-if="showSpecificColourKnown" class="detailed-colour__btn" @click="specificColourKnown = 'true'"> {{specificColourButtonText}}</button>

    <div class="tla-form-group" v-if="showSpecficColourOptions">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="specificColourLabel"></label>

      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in specificColourOptions"
          :key="`${item.touchpointValue}-${basicColour}-${engineSize}`"
          :collectionName="'specificColours'"
          :inputId="`${item.touchpointValue}-${basicColour}-${engineSize}`"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isFullWidth="true"
          :validationScope="validationScope"
          @specificColours="setSpecificColourChoice($event)">
          <div class="generic-touchpoint__text" slot="touchpoint" v-html="item.touchpointName"></div>
        </touchpoint>

        <touchpoint
          :collectionName="'specificColours'"
          :key="`NotSure-${basicColour}-${engineSize}`"
          :inputId="`NotSure-${basicColour}-${engineSize}`"
          :inputValue="'Not Sure'"
          :isSelected="false"
          :isFullWidth="true"
          :validationScope="validationScope"
          @specificColours="setSpecificColourChoice($event)">
        <div class="generic-touchpoint__text" slot="touchpoint" v-html="'Not Sure'"></div>
      </touchpoint>
      </div>
      <span class="tla-form-group__message " :class="getSpecificValidationClass('message')">
        Please select an option.
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Inject } from 'vue-property-decorator';
import VeeValidate, { Validator, FieldError } from 'vee-validate';
import { validationService } from '@/services/ValidationService';
import SearchQuery from '@/models/SearchQuery';
import GenericTouchpointComponent from '../ui/GenericTouchPointComponent.vue';
import Touchpoint from '../../models/Touchpoint';
import ExtraData from '../../models/ExtraData';
import Colour from '../../models/vehicle/colour';
import IDerivative from '../../models/vehicle/interfaces/derivative.d';

@Component({
  components: {
  touchpoint: GenericTouchpointComponent
  }
  })
export default class DetailedColourComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'Pick your colour <span>*</span>' })
  public basicColourLabel!: string;

  @Prop({ default: 'Based on available manufacturer options' })
  public colourRestrictionText!: string;

  @Prop({ default: null })
  public validationScope!: string;

  private colourKnownLabel: string = 'Do you have a colour in mind? <span>*</span>';

  private specificColourButtonText: string = 'Choose specific shade';

  private specificColourLabel: string = 'Choose your specific shade <span>*</span>';

  private fakeErrorId: string = 'colour-known-not-selected';

  private basicColourKnown: string | null = null;

  private specificColourKnown: string | null = null;

  private colourKnownOptions: Touchpoint[] = [
    new Touchpoint('true', 'Yes'),
    new Touchpoint('false', 'No')
  ];

  public getKnownValidationClass(fieldType: string) {
    if (this.basicColourKnown !== null && this.basicColourKnown.length > 0) {
      return '';
    }

    return this.getValidationClass('basicColourKnown', fieldType);
  }

  public getBasicValidationClass(fieldType: string) {
    if (this.basicColour !== null && this.basicColour.length > 0) {
      return '';
    }

    return this.getValidationClass('basicColours', fieldType);
  }

  public getSpecificValidationClass(fieldType: string) {
    if (this.specificColour !== null && this.specificColour.length > 0) {
      return '';
    }

    return this.getValidationClass('specificColours', fieldType);
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public get colours() {
    return this.$store.getters['vehicleSearchModule/colours'];
  }

  public get searchQuery(): SearchQuery {
    return this.$store.getters['searchQueryModule/searchQuery'];
  }

  public get engineSize(): number | null {
    return this.searchQuery.engineSize;
  }

  public get derivatives(): IDerivative[] {
    return this.$store.getters['vehicleSearchModule/derivatives'];
  }

  public formatImageName(value: string): string {
    return value.toLowerCase().replace(/ /g, '-');
  }

  public get imagePath() {
    return `${process.env.BASE_URL}images/detailed-colours`;
  }

  public get basicColourOptions(): Touchpoint[] {
    return Object.keys(this.colours)
      .map((x: string) => new Touchpoint(x, `<img src="${this.imagePath}/${this.formatImageName(x)}.png"/> ${x}`));
  }

  public get specificColourOptions(): Touchpoint[] {
    const options: Touchpoint[] = [];

    if (this.basicColour) {
      const specificColours = this.colours[this.basicColour];
      if (specificColours) {
        specificColours.forEach((item) => {
          options.push(new Touchpoint(item, item));
        });
      }
    }

    return options;
  }

  public get showComponent(): boolean {
    if (this.colours) {
      return Object.keys(this.colours).length > 0;
    }

    return false;
  }

  public get showBasicColours(): boolean {
    return this.basicColourKnown === 'true';
  }

  public get showSpecificColourKnown(): boolean {
    return this.basicColour !== null && this.basicColour.length > 0;
  }

  public get showSpecficColourOptions(): boolean {
    return this.specificColourKnown === 'true' && this.basicColour !== null && this.basicColour.length > 0;
  }

  public get basicColour(): string | null {
    return this.searchQuery.basicColour;
  }

  public get specificColour(): string | null {
    return this.searchQuery.specificColour;
  }

  public async setBasicColourKnownValue(value: string | null) {
    this.basicColourKnown = value;

    if (value) {
      const extraData = new ExtraData('basicColourKnown', value);
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'basicColourKnown');
    }
  }

  public async setBasicColourValue(value: string | null) {
    await this.$store.dispatch('vehicleModule/submitBasicColour', value);

    if (value) {
      const extraData = new ExtraData('basicColour', value);
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'basicColour');
    }
  }

  public async setSpecificColourKnownValue(value: string | null) {
    this.specificColourKnown = value;

    if (value) {
      const extraData = new ExtraData('specificColourKnown', value);
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'specificColourKnown');
    }
  }

  public async setSpecificColourValue(value: string | null) {
    await this.$store.dispatch('vehicleModule/submitSpecificColour', value);

    if (value) {
      const extraData = new ExtraData('specificColour', value);
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    } else {
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'specificColour');
    }
  }

  public async setBasicColourKnownChoice(value: string | null) {
    this.removeErrors();

    await this.resetBasicColourKnownChildValues();
    await this.setBasicColourKnownValue(value);

    this.$store.dispatch('vehicleModule/submitDerivative', this.derivatives[0]);
  }

  public async setBasicColourChoice(value: string | null) {
    await this.resetBasicColourChildValues();
    await this.setBasicColourValue(value);

    this.$store.dispatch('vehicleModule/submitDerivative', this.derivatives[0]);
  }

  public async setSpecificColourKnownChoice(value: string | null) {
    await this.resetSpecificColourKnownChildValues();
    await this.setSpecificColourKnownValue(value);
  }

  public async setSpecificColourChoice(value: string | null) {
    await this.setSpecificColourValue(value);
  }

  public async resetBasicColourKnownChildValues() {
    await this.setBasicColourValue(null);
    await this.setSpecificColourKnownValue(null);
    await this.setSpecificColourValue(null);
  }

  public async resetBasicColourChildValues() {
    await this.setSpecificColourKnownValue(null);
    await this.setSpecificColourValue(null);
  }

  public async resetSpecificColourKnownChildValues() {
    await this.setSpecificColourValue(null);
  }

  public async resetAllValues() {
    await this.setBasicColourKnownValue(null);
    await this.resetBasicColourKnownChildValues();

    if (this.showComponent) {
      this.$validator.errors.add({
        field: this.fakeErrorId,
        msg: 'Please answer.',
        scope: this.validationScope,
        id: this.fakeErrorId
      });
    } else {
      this.removeErrors();
      this.$store.dispatch('vehicleModule/submitDerivative', this.derivatives[0]);
    }
  }

  private removeErrors() {
    this.$validator.errors.remove('basicColours', this.validationScope);
    this.$validator.errors.remove('specificColours', this.validationScope);
    this.$validator.errors.removeById(this.fakeErrorId);
  }

  public created() {
    this.$events.on('resetVehicleColours', this.resetAllValues);
  }
}
</script>
